import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
    static targets = ['submitButton']

    static values = {
        submitUrl: String,
    }

    declare readonly submitButtonTarget: HTMLButtonElement
    declare readonly hasSubmitButtonTarget: boolean
    declare readonly submitUrlValue: string

    private submitting = false

    connect() {
        this.updateSubmitButton()
    }

    updateSubmitButton() {
        if (!this.hasSubmitButtonTarget) {
            return
        }

        const fileInput = this.element.querySelector('input[type="file"]') as HTMLInputElement | null

        this.submitButtonTarget.disabled = !fileInput?.files?.length
    }

    async submit(event: Event) {
        event.preventDefault()

        if (this.submitting) {
            return
        }

        this.clearErrors()
        this.setSubmitting(true)

        const formData = this.buildFormData()

        try {
            const response = await fetch(this.submitUrlValue, {
                method: 'POST',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: formData,
            })

            const data = await response.json()

            if (response.ok && data.success) {
                this.reset()
                this.showAlert('Заявка принята. Мы свяжемся с вами после обработки прайс-листа', 'success', 5000)

                return
            }

            if (data.error) {
                this.showAlert(data.error, 'error', 5000)
            } else if (data.errors) {
                this.applyErrors(data.errors)
            } else {
                this.showAlert('Произошла ошибка при отправке. Попробуйте позже', 'error', 5000)
            }
        } catch {
            this.showAlert('Произошла ошибка при отправке. Попробуйте позже', 'error', 5000)
        } finally {
            this.setSubmitting(false)
        }
    }

    cancel() {
        this.reset()
    }

    private showAlert(message: string, type: string, timeout?: number) {
        window.dispatchEvent(new CustomEvent('alert:show', {
            detail: { message, type, timeout },
            bubbles: true,
        }))
    }

    private buildFormData(): FormData {
        const formData = new FormData()

        const tokenInput = this.element.querySelector('input[name$="[_token]"]') as HTMLInputElement | null

        if (tokenInput) {
            formData.append(tokenInput.name, tokenInput.value)
        }

        const fileInput = this.element.querySelector('input[type="file"]') as HTMLInputElement | null

        if (fileInput?.files) {
            for (const file of Array.from(fileInput.files)) {
                formData.append(fileInput.name, file)
            }
        }

        return formData
    }

    private applyErrors(errors: string[] | Record<string, string>) {
        if (Array.isArray(errors)) {
            return
        }

        for (const [fieldName, message] of Object.entries(errors)) {
            const field = this.element.querySelector(`[name$="[${fieldName}]"]`) as HTMLInputElement | null

            if (!field) {
                continue
            }

            if (field.type === 'file') {
                field.closest('[data-controller~="file-input"]')
                    ?.dispatchEvent(new CustomEvent('file-input:add-error', { detail: { message } }))

                continue
            }

            field.dispatchEvent(new CustomEvent('input:add-error', { detail: { message }, bubbles: true }))
        }
    }

    private clearErrors() {
        this.element.querySelectorAll('[data-controller~="file-input"]').forEach((wrapper) => {
            wrapper.dispatchEvent(new CustomEvent('file-input:clear-error'))
        })
    }

    private reset() {
        this.element.querySelectorAll('[data-controller~="file-input"]').forEach((wrapper) => {
            wrapper.dispatchEvent(new CustomEvent('file-input:reset'))
        })

        this.clearErrors()
        this.updateSubmitButton()
    }

    private setSubmitting(submitting: boolean) {
        this.submitting = submitting

        if (!this.hasSubmitButtonTarget) {
            return
        }

        this.submitButtonTarget.disabled = submitting
    }
}
